import { DateTime } from "luxon";

import { currencyFormatLong } from "components/app_layout/Typography/Numbers";
import { ClipboardContent } from "lib/clipboard";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { tenderStages } from "lib/types/models";
import { formatAmount, formatDate } from "lib/utils";

/** Roughly what fits a pasted chat message without swamping it. Matches the share email. */
export const SHARED_DESCRIPTION_MAX_LENGTH = 500;

type ShareableNotice = Pick<
  NoticeDetails,
  | "name"
  | "stagev2"
  | "value"
  | "currency"
  | "publishDate"
  | "closeDate"
  | "awardDate"
  | "description"
  | "url"
> & { buyers: { name?: string | null }[] };

// The share email's date format, so a copied notice and an emailed one read the same
const SHARE_DATE_FORMAT = "d MMM yyyy";

/**
 * A notice as text to paste into Teams, Slack or an email, laid out like the share email. Only facts
 * published on the notice: nothing about the user's company (signals, AI summary, leads) and no buyer
 * contacts. It copies what the page shows: an award the user can't see in full arrives from the API
 * with its details already removed.
 */
export function buildNoticeShareDetails(
  notice: ShareableNotice,
  stotlesUrl: string,
): Required<ClipboardContent> {
  const title = notice.name ?? "Notice";
  // The first buyer is the lead buyer, and the one the share email names
  const buyerName = notice.buyers[0]?.name;
  const stageLabel = notice.stagev2 ? tenderStages[notice.stagev2]?.label : undefined;

  const facts: [string, string][] = [];
  if (notice.value) facts.push(["Value", formatValue(notice.value, notice.currency)]);
  if (notice.publishDate) facts.push(["Published", formatShareDate(notice.publishDate)]);
  if (notice.closeDate) facts.push(["Closing date", formatShareDate(notice.closeDate)]);
  if (notice.awardDate) facts.push(["Award date", formatShareDate(notice.awardDate)]);

  const description = shortenDescription(notice.description);
  const sourceUrl = httpUrlOrNull(notice.url);

  // Lines within a group sit together; groups are separated by a blank line
  const summary = [
    stageLabel ? `${stageLabel}: ${title}` : title,
    buyerName,
    ...facts.map(([label, value]) => `${label}: ${value}`),
  ]
    .filter(Boolean)
    .join("\n");
  const links = [
    `View in Stotles: ${stotlesUrl}`,
    sourceUrl && `View the original notice: ${sourceUrl}`,
  ]
    .filter(Boolean)
    .join("\n");
  const plain = [summary, description, links].filter(Boolean).join("\n\n");

  const heading = [
    stageLabel && escapeHtml(stageLabel),
    `<strong><a href="${escapeHtml(stotlesUrl)}">${escapeHtml(title)}</a></strong>`,
    buyerName && escapeHtml(buyerName),
  ]
    .filter(Boolean)
    .join("<br>");
  const html = [
    `<p>${heading}</p>`,
    facts.length
      ? `<p>${facts.map(([label, value]) => `${label}: ${escapeHtml(value)}`).join("<br>")}</p>`
      : "",
    description ? `<p>${escapeHtml(description)}</p>` : "",
    `<p>${[
      `<a href="${escapeHtml(stotlesUrl)}">View in Stotles</a>`,
      sourceUrl && `<a href="${escapeHtml(sourceUrl)}">View the original notice</a>`,
    ]
      .filter(Boolean)
      .join(" · ")}</p>`,
  ]
    .filter(Boolean)
    .join("");

  return { plain, html };
}

/** Whitespace collapsed, then cut at the last word before the limit with an ellipsis. */
export function shortenDescription(
  description: string | null | undefined,
  maxLength = SHARED_DESCRIPTION_MAX_LENGTH,
): string | null {
  const text = description?.replace(/\s+/g, " ").trim();
  if (!text) return null;
  if (text.length <= maxLength) return text;

  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  const words = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${words.replace(/[\s.,;:!?-]+$/, "")}…`;
}

// Parsed as local time, so "2026-11-18" is that calendar day wherever the user is. `new Date()`
// would read it as UTC midnight and show the day before west of UTC.
function formatShareDate(value: string): string {
  return formatDate(DateTime.fromISO(value).toJSDate(), "en-GB", SHARE_DATE_FORMAT);
}

// The full amount, as the share email shows it. With no currency there's nothing to claim, so
// it's a plain number rather than pounds.
function formatValue(value: number, currency: string | null | undefined): string {
  return currency ? currencyFormatLong(value, currency) : formatAmount(value);
}

function httpUrlOrNull(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;") // must be first, or later entities get double-escaped
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
